import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Marketing Pool (docs/notes/MARKETING_POOL_SPEC.md §12/§14) — Global Health only,
 * never public. Every right is enforced by the backend (MarketingPoolAccess); the
 * frontend only hides what a user cannot use.
 *
 * Member API  /api/marketing_pool/*        (Api::MarketingPoolController)
 * Admin API   /api/admin/marketing_pool/*  (Api::AdminMarketingPoolController)
 *
 * Founding Partner model, contract v0.7 as corrected by the customer on
 * 2026-10-28: every unlocked partner enters their OWN participation amount
 * (min 500 USD); the total payout is amount × the payout factor management sets
 * centrally. POST /investments creates the partner's own draft (status
 * `offered`), which they then accept on the same page. There is no office offer
 * any more, and no membership precondition of any kind. A partner sees only their
 * own contract data and the published factor — no commissions.
 *
 * Amounts arrive as 2-decimal strings and are sent the same way — no float maths
 * on money.
 */

export type MpStatus = 'offered' | 'pending' | 'locked' | 'active' | 'due' | 'closed' | 'withdrawn' | 'reversed';
export type MpPaymentMethod = 'bank_transfer' | 'crypto';
export type MpToken = 'USDT' | 'USDC';
/** `referrer_3x` only ever reaches the admin view (and the referrer's own referral rows). */
export type MpObligationKind = 'investor_total' | 'referrer_3x';
export type MpObligationStatus = 'planned' | 'due' | 'partially_paid' | 'paid' | 'cancelled';

/** `user_payload.marketing_pool` — explicit booleans, fail-closed. */
export interface MarketingPoolPayload {
  visible:    boolean;
  can_invest: boolean;
  referrer:   boolean;
  can_unlock: boolean;
  can_manage: boolean;
  /** Not sent by the backend today (see report): holder of existing investments. */
  holder?:    boolean;
}

export interface MpObligation {
  id:                  number;
  investment_id:       number;
  kind:                MpObligationKind;
  beneficiary_user_id: number;
  amount_total:        string;
  amount_paid:         string;
  amount_open:         string;
  due_at:              string | null;
  status:              MpObligationStatus;
  closed_at:           string | null;
}

export interface MpPayout {
  id:                     number;
  obligation_id:          number;
  amount:                 string;
  currency:               string;
  approved_by_id:         number | null;
  approved_at:            string | null;
  emergency_override:     boolean;
  override_reason:        string | null;
  ewallet_transaction_id: number | null;
  reference:              string | null;
  note:                   string | null;
}

export interface MpInvestment {
  id:                  number;
  /** The contract's "Investment-ID". */
  reference:           string;
  status:              MpStatus;
  amount_usd:          string;
  /** amount × payout_factor, computed by the backend (the participation amount is included in it). */
  total_payout_usd:    string;
  /** The payout factor the total was computed with (null on agreements from before 2026-10-28). */
  payout_factor:       string | null;
  /**
   * Where `payout_factor` came from when the draft was made: the central factor or the
   * partner's personal one (Walter 2026-10-29: personal factor as an exception). Null on
   * agreements from before that.
   */
  payout_factor_source: 'central' | 'personal' | null;
  /** Null while `offered`: the partner chooses at acceptance. */
  payment_method:      MpPaymentMethod | null;
  payment_reference:   string | null;
  bank:                Record<string, string> | null;
  crypto: null | {
    token: MpToken; network: string; amount_received: string | null; usd_assigned: string | null; tx_hash: string | null;
  };
  order_id:            number | null;
  order_status:        string | null;
  accepted_at:         string | null;
  paid_at:             string | null;
  confirmed_at:        string | null;
  withdrawal_ends_at:  string | null;
  waiver:              null | { at: string; version: string };
  released_at:         string | null;
  due_at:              string | null;
  /** = due_at: the regular payout date. */
  payout_date:         string | null;
  /** = paid_at: the contract starts with the confirmed payment. */
  term_start:          string | null;
  term_end:            string | null;
  withdrawable:        boolean;
  paid_total:          string | null;
  open_total:          string | null;
  contract_version:    string | null;
  contract_locale:     string | null;
  /** Only the partner's own (`investor_total`). */
  obligations:         MpObligation[];
  payouts:             MpPayout[];
  documents:           string[];
  closed_at:           string | null;
  withdrawn_at:        string | null;
  reversed_at:         string | null;
  created_at:          string;
}

export interface MpReferral {
  id:         number;
  reference:  string;
  status:     MpStatus;
  investor:   { id: number; name: string | null };
  amount_usd: string;
  paid_at:    string | null;
  released_at: string | null;
  due_at:     string | null;
  commission: { amount: string | null; status: 'open' | 'held' | 'booked' };
  claim:      MpObligation | null;
}

export interface MpOverview {
  visible:              boolean;
  /** Pool activated and contracts released: a draft can be accepted. */
  can_accept:           boolean;
  /** visible && pool open && payout factor set: a new participation can be entered. */
  can_invest:           boolean;
  /**
   * The factor that applies to THIS partner, e.g. "6.00": their personal factor when management
   * set one (Walter 2026-10-29), else the central one; null while neither is set.
   */
  payout_factor:        string | null;
  /** True when `payout_factor` is the partner's personal factor (the central one is never shown then). */
  payout_factor_personal: boolean;
  open_for_investments: boolean;
  referrer:             boolean;
  minimum_amount:       string;
  withdrawal_days:      number;
  term_days:            number;
  payment_methods:      MpPaymentMethod[];
  crypto_tokens:        MpToken[];
  crypto_network:       string;
  investments:          MpInvestment[];
  referrals:            MpReferral[];
  server_time:          string;
}

export interface MpDocument {
  key:      string;
  version:  string;
  locale:   string;
  title:    string;
  /** Translation key of the checkbox label (`marketing_pool.checkbox.<key>`). */
  checkbox?: string;
  required: boolean;
  draft:    boolean;
  html?:    string;
}

/** POST /investments/:id/accept — the partner accepts their own draft. */
export interface MpAcceptRequest {
  payment_method: MpPaymentMethod;
  crypto_token?:  MpToken;
  language:       string;
  declarations:   { us_citizen: boolean; citizenship_country: string; residence_country: string };
  acceptances:    { key: string; version: string }[];
  waiver:         boolean;
}

export type MpPaymentInstructions =
  | { method: 'bank_transfer'; order_id: number; amount: string; currency: string; reference: string;
      bank: Record<string, string> | null }
  | { method: 'crypto'; order_id: number; amount: string; token: MpToken; network: string; next_step: string };

export interface MpAcceptResponse {
  investment: MpInvestment;
  payment:    MpPaymentInstructions;
}

export interface MpAcceptance {
  document_key:     string;
  document_version: string;
  document_locale:  string;
  accepted_at:      string;
  country:          string | null;
}

// ----- Admin ----------------------------------------------------------------------

export interface MpUserBrief { id: number; email: string | null; name?: string | null }

export interface MpAdminOverview {
  investments: {
    count: number; by_status: Record<string, number>; by_payment_method: Record<string, number>;
    confirmed_total: string; open_total: string; locked_total: string; active_total: string;
    withdrawn_total: string; reversed_total: string;
  };
  aufbau_pool: {
    inflows_by_source: Record<string, string>; inflows_total: string; payouts_total: string; ledger_net: string;
    account_balances: Record<string, string>;
  };
  company_cash: { investment_commission_company: string };
  commissions:  { referrer_total: string; company_total: string };
  obligations: {
    investor_total: string; investor_open: string; referrer_total: string; referrer_open: string; paid_total: string;
    due_within_30: string; due_within_60: string; due_within_90: string; overdue: string; due_count: number;
  };
  paid_out:         { investor: string; referrer: string };
  exceptions_count: number;
  settings:         MpSettings;
}

export interface MpSettings {
  activated_at:         string | null;
  activated_timezone:   string | null;
  activated_by_id:      number | null;
  build_phase_ends_at:  string | null;
  build_phase_active:   boolean;
  contracts_released:   boolean;
  open_for_investments: boolean;
  country_blocklist:    string[];
  /** Customer correction 2026-10-28: total payout = amount × this factor. Null = not set. */
  payout_factor:        string | null;
  /** Pool open AND factor set: partners can conclude participations on their own. */
  self_service_ready:   boolean;
}

export interface MpAdminListRow {
  id: number; reference: string; status: MpStatus; amount_usd: string; total_payout_usd: string;
  payment_method: MpPaymentMethod | null; payment_reference: string | null; user: MpUserBrief;
  referrer: { id: number; email: string | null; eligible: boolean | null } | null;
  commission_to: string | null; accepted_at: string | null; paid_at: string | null; withdrawal_ends_at: string | null;
  released_at: string | null; due_at: string | null; manual: boolean; booked_by_id: number | null;
  confirmed_by: string | null; investor_claim: string | null; referrer_claim: string | null; created_at: string;
}

export interface MpAdminList {
  investments: MpAdminListRow[]; total_count: number; page: number; per_page: number;
  statuses: string[]; payment_methods: string[];
}

export interface MpLedgerEntry {
  id: number; entry_type: string; account: string; amount_usd: string; currency: string | null;
  investment_id: number | null; order_id: number | null; product_code: string | null; obligation_id: number | null;
  payout_id: number | null; ewallet_transaction_id: number | null; admin_id: number | null; reason: string | null;
  reversal_of_id: number | null; created_at: string;
}

export interface MpAuditEvent {
  id: number; subject_type: string; subject_id: number; action: string; reason: string | null;
  changes: Record<string, unknown> | null; admin_id: number | null; admin_name: string | null; created_at: string;
}

/** Admin detail: the member JSON plus the internal fields (never shown to a partner). */
export interface MpAdminInvestment extends MpInvestment {
  amounts: {
    aufbau_amount:     string | null;
    commission_amount: string | null;
    commission_to:     'referrer' | 'company' | null;
    referrer_claim:    string | null;
    investor_claim?:   string | null;
  };
  user: MpUserBrief; referrer: MpUserBrief | null; referrer_eligible: boolean | null; referrer_checked_at: string | null;
  confirmed_by: string | null; booked_by: MpUserBrief | null; booking_note: string | null;
  declarations: {
    us_citizen: boolean | null; citizenship_country: string | null; residence_country: string | null;
    declared_at: string | null; declared_ip: string | null; declared_user_agent: string | null;
  };
  reversal_reason: string | null;
  refund: null | { reference: string; at: string; by_id: number };
  ledger: MpLedgerEntry[];
}

export interface MpAdminInvestmentDetail {
  investment:  MpAdminInvestment;
  acceptances: Record<string, unknown>[];
  audit:       MpAuditEvent[];
}

export interface MpAdminObligation extends MpObligation {
  investment_reference: string;
  investment_status:    MpStatus;
  beneficiary:          { id: number; email: string | null };
  payouts:              MpPayout[];
}

export interface MpPermission {
  id: number; user: MpUserBrief | null; kind: string; active: boolean; granted_by_id: number | null;
  granted_at: string | null; reason: string | null; revoked_by_id: number | null; revoked_at: string | null;
  revoke_reason: string | null;
}

export interface MpPermissionsResponse {
  permissions: MpPermission[]; kinds: string[]; grantable_kinds: string[]; my_kinds: string[];
  /** Management only (`manage_settings`) — never a structure delegate: may set personal payout factors. */
  can_manage_factor: boolean;
}

/** An active personal payout factor (Walter 2026-10-29: personal factor as an exception to the central one). */
export interface MpFactorOverride {
  id:             number;
  user:           { id: number; email: string | null; name: string | null };
  payout_factor:  string;
  reason:         string | null;
  granted_by:     { id: number; email: string | null } | null;
  created_at:     string;
  revoked_at:     string | null;
  revoked_by:     { id: number; email: string | null } | null;
  revoke_reason:  string | null;
}

export interface MpFactorOverridesResponse {
  /** Active rows only, newest first. */
  overrides:         MpFactorOverride[];
  central_factor:    string | null;
  can_manage_factor: boolean;
}

export interface MpSettingsResponse {
  settings: MpSettings; documents: MpDocument[]; unreleased_documents: string[];
  can_manage_settings: boolean; can_activate: boolean;
}

export interface MpUserLookup {
  id: number; email: string; name: string; sponsor_id: number | null;
  /** The person's personal payout factor; only present for callers who may manage settings. */
  factor_override?: string | null;
}

/** Minimum reason length of every manual admin action (backend Rules.valid_reason). */
export const MP_MIN_REASON = 10;

/** Error codes with their own text under `marketing_pool.errors.<code>`. */
export const MP_ERROR_CODES = [
  'not_found', 'forbidden', 'marketing_pool_not_activated', 'contracts_not_released', 'not_offered', 'amount_invalid',
  'amount_below_minimum', 'payout_factor_missing', 'payout_factor_invalid', 'factor_changed',
  'payment_method_invalid', 'crypto_token_invalid',
  'declaration_required', 'acceptance_required', 'us_citizen_excluded',
  'us_resident_excluded', 'country_blocked', 'version_conflict', 'documents_unavailable',
  'withdrawal_not_possible', 'document_generation_failed', 'reason_required', 'user_not_found', 'not_in_own_structure',
  'already_confirmed',
  'crypto_amount_required', 'incomplete_payment', 'crypto_tx_hash_required', 'self_approval_forbidden',
  'four_eyes_required', 'override_not_allowed', 'override_reason_required', 'paid_at_invalid', 'paid_at_in_future',
  'reference_required', 'obligation_closed', 'investment_not_payable', 'amount_exceeds_open',
  'insufficient_pool_funds', 'payout_transfer_failed', 'currency_invalid', 'source_invalid',
  'insufficient_company_funds', 'deposit_transfer_failed', 'kind_invalid', 'already_granted', 'already_revoked',
  'documents_not_final', 'no_changes', 'settings_invalid', 'timezone_invalid', 'already_activated',
  'not_withdrawn', 'refund_reference_required', 'refund_already_recorded', 'not_reversible', 'product_missing'
] as const;

/** The API error code of a failed call, or null. */
export function mpErrorCode(err: unknown): string | null {
  const e = err as HttpErrorResponse | undefined;
  const code = e?.error?.error ?? e?.error?.code;
  return typeof code === 'string' ? code : null;
}

/** Translation key of an API error (falls back to the generic text). */
export function mpErrorKey(err: unknown): string {
  const code = mpErrorCode(err);
  if (code && (MP_ERROR_CODES as readonly string[]).includes(code)) return `marketing_pool.errors.${code}`;
  if ((err as HttpErrorResponse | undefined)?.status === 403) return 'marketing_pool.errors.forbidden';
  return 'marketing_pool.errors.generic';
}

/** Interpolation values for an error text (minimum, open amount …). */
export function mpErrorParams(err: unknown): Record<string, string> {
  const details = (err as HttpErrorResponse | undefined)?.error?.details;
  const out: Record<string, string> = {};
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    Object.entries(details).forEach(([k, v]) => { out[k] = String(v); });
  } else if (Array.isArray(details)) {
    out['list'] = details.map(d => (typeof d === 'object' ? JSON.stringify(d) : String(d))).join(', ');
  } else if (details !== undefined && details !== null) {
    out['list'] = String(details);
  }
  return out;
}

function cleanParams(filters: Record<string, unknown>): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params[key] = String(value);
  });
  return params;
}

@Injectable({ providedIn: 'root' })
export class MarketingPoolService {
  private http = inject(HttpClient);

  // ----- Member ---------------------------------------------------------------------

  /** 404 unless visible, holder or referrer. */
  overview(): Observable<MpOverview> {
    return this.http.get<MpOverview>('/marketing_pool/overview');
  }

  /** The contract; with `investmentId` it carries that partner's individual data. */
  documents(language: string, investmentId?: number): Observable<{ language: string; documents: MpDocument[] }> {
    const params: Record<string, string> = { language };
    if (investmentId) params['investment_id'] = String(investmentId);
    return this.http.get<{ language: string; documents: MpDocument[] }>('/marketing_pool/documents', { params });
  }

  /**
   * The partner's own draft (status `offered`) for `amount` — the backend computes
   * the total payout from the current factor and withdraws the partner's previous
   * unaccepted draft (customer correction 2026-10-28).
   */
  createInvestment(amount: string): Observable<{ investment: MpInvestment }> {
    return this.http.post<{ investment: MpInvestment }>('/marketing_pool/investments', { amount });
  }

  accept(id: number, body: MpAcceptRequest): Observable<MpAcceptResponse> {
    return this.http.post<MpAcceptResponse>(`/marketing_pool/investments/${id}/accept`, body);
  }

  investment(id: number): Observable<{ investment: MpInvestment; acceptances: MpAcceptance[] }> {
    return this.http.get<{ investment: MpInvestment; acceptances: MpAcceptance[] }>(`/marketing_pool/investments/${id}`);
  }

  /** Withdrawal within the period — or discarding an `offered` draft. */
  withdraw(id: number): Observable<{ investment: MpInvestment }> {
    return this.http.post<{ investment: MpInvestment }>(`/marketing_pool/investments/${id}/withdraw`, {});
  }

  /**
   * As a blob through HttpClient: on the hybrid build the JWT travels in an
   * Authorization header a plain <a href> would not carry.
   */
  documentPdf(id: number, type: string, language: string): Observable<Blob> {
    return this.http.get(`/marketing_pool/investments/${id}/documents/${encodeURIComponent(type)}.pdf`,
                         { params: { language }, responseType: 'blob' });
  }

  referrals(): Observable<{ referrals: MpReferral[] }> {
    return this.http.get<{ referrals: MpReferral[] }>('/marketing_pool/referrals');
  }

  // ----- Admin ----------------------------------------------------------------------

  adminOverview(): Observable<MpAdminOverview> {
    return this.http.get<MpAdminOverview>('/admin/marketing_pool/overview');
  }

  adminInvestments(filters: Record<string, unknown>): Observable<MpAdminList> {
    return this.http.get<MpAdminList>('/admin/marketing_pool/investments', { params: cleanParams(filters) });
  }

  adminExport(filters: Record<string, unknown>): Observable<Blob> {
    return this.http.get('/admin/marketing_pool/investments/export.xlsx',
                         { params: cleanParams(filters), responseType: 'blob' });
  }

  adminInvestment(id: number): Observable<MpAdminInvestmentDetail> {
    return this.http.get<MpAdminInvestmentDetail>(`/admin/marketing_pool/investments/${id}`);
  }

  adminReverse(id: number, reason: string): Observable<{ investment: MpAdminInvestment }> {
    return this.http.post<{ investment: MpAdminInvestment }>(`/admin/marketing_pool/investments/${id}/reverse`, { reason });
  }

  adminRefund(id: number, reference: string, reason: string): Observable<{ investment: MpAdminInvestment }> {
    return this.http.post<{ investment: MpAdminInvestment }>(`/admin/marketing_pool/investments/${id}/refund`,
                                                             { reference, reason });
  }

  /** Any `pending` (accepted, awaiting payment) investment; four eyes (never one's own participation). */
  adminConfirmPayment(id: number, body: Record<string, unknown>): Observable<{ investment: MpAdminInvestment }> {
    return this.http.post<{ investment: MpAdminInvestment }>(`/admin/marketing_pool/investments/${id}/confirm_payment`, body);
  }

  adminObligations(filters: Record<string, unknown>): Observable<{ obligations: MpAdminObligation[]; total_count: number; page: number; per_page: number }> {
    return this.http.get<{ obligations: MpAdminObligation[]; total_count: number; page: number; per_page: number }>(
      '/admin/marketing_pool/obligations', { params: cleanParams(filters) });
  }

  adminPayout(obligationId: number, body: Record<string, unknown>): Observable<{ payouts: MpPayout[]; obligation: MpObligation; investment_status: MpStatus }> {
    return this.http.post<{ payouts: MpPayout[]; obligation: MpObligation; investment_status: MpStatus }>(
      `/admin/marketing_pool/obligations/${obligationId}/payouts`, body);
  }

  adminDeposit(body: Record<string, unknown>): Observable<{ ledger_entry: MpLedgerEntry }> {
    return this.http.post<{ ledger_entry: MpLedgerEntry }>('/admin/marketing_pool/deposits', body);
  }

  adminPermissions(filters: Record<string, unknown> = {}): Observable<MpPermissionsResponse> {
    return this.http.get<MpPermissionsResponse>('/admin/marketing_pool/permissions', { params: cleanParams(filters) });
  }

  adminGrant(userId: number, kind: string, reason: string): Observable<{ permission: MpPermission }> {
    return this.http.post<{ permission: MpPermission }>('/admin/marketing_pool/permissions', { user_id: userId, kind, reason });
  }

  adminRevoke(id: number, reason: string): Observable<{ permission: MpPermission }> {
    return this.http.delete<{ permission: MpPermission }>(`/admin/marketing_pool/permissions/${id}`, { body: { reason } });
  }

  /** 403 for anyone without `manage_settings`. */
  adminFactorOverrides(): Observable<MpFactorOverridesResponse> {
    return this.http.get<MpFactorOverridesResponse>('/admin/marketing_pool/factor_overrides');
  }

  /** Sets (or replaces) the person's personal factor; applies to new participations only. */
  adminSetFactorOverride(userId: number, body: { payout_factor: string; reason: string }): Observable<{ override: MpFactorOverride }> {
    return this.http.put<{ override: MpFactorOverride }>(`/admin/marketing_pool/users/${userId}/factor_override`, body);
  }

  /** Removes the personal factor: the central one applies to this person again. */
  adminClearFactorOverride(userId: number, reason: string): Observable<{ override: MpFactorOverride }> {
    return this.http.delete<{ override: MpFactorOverride }>(`/admin/marketing_pool/users/${userId}/factor_override`,
                                                            { body: { reason } });
  }

  adminSettings(): Observable<MpSettingsResponse> {
    return this.http.get<MpSettingsResponse>('/admin/marketing_pool/settings');
  }

  adminUpdateSettings(body: { country_blocklist?: string[]; contracts_released?: boolean; payout_factor?: string; reason: string }): Observable<{ settings: MpSettings }> {
    return this.http.put<{ settings: MpSettings }>('/admin/marketing_pool/settings', body);
  }

  adminActivate(timezone: string, reason: string): Observable<{ settings: MpSettings }> {
    return this.http.post<{ settings: MpSettings }>('/admin/marketing_pool/settings/activate', { timezone, reason });
  }

  adminAudit(filters: Record<string, unknown>): Observable<{ events: MpAuditEvent[]; total_count: number; page: number; per_page: number }> {
    return this.http.get<{ events: MpAuditEvent[]; total_count: number; page: number; per_page: number }>(
      '/admin/marketing_pool/audit', { params: cleanParams(filters) });
  }

  adminExceptions(): Observable<{ exceptions: MpAuditEvent[] }> {
    return this.http.get<{ exceptions: MpAuditEvent[] }>('/admin/marketing_pool/exceptions');
  }

  adminUsers(q: string): Observable<{ users: MpUserLookup[] }> {
    return this.http.get<{ users: MpUserLookup[] }>('/admin/marketing_pool/users', { params: { q } });
  }
}

/** Triggers a browser download of a blob. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
